import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import dynamic from 'next/dynamic';

import { scaleLinear } from 'd3-scale';
import { followCursor } from 'tippy.js/headless';

import useFormatNumber, { getNumberFormat } from '@/hooks/format/useFormatNumber';

//Libs
import { worldMap } from '@/utils/chartmaps/world-110m';
//Hooks
import useDarkMode from '@/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

import { darkModeColorsChart } from '@/lib/analytics/colors';

import { SizeMe } from '@/components/base';
//Components
import Tooltip from '@/components/base/Tooltip';
import ChartWrapper from '@/components/composite/Charts/ChartWrapper';
import { TooltipMapChart } from '@/components/composite/Charts/components/TooltipMapChart';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const BubbleMapChartComponent = ({
    data,
    columnNames,
    activeTable,
    userNumbersFormat,
    height = 260,
    isExport
}) => {
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const [tooltipContent, setTooltipContent] = useState(null);

    const myArray = data.graphData;
    let lowest = 0;
    let highest = 0;
    let tmp;
    for (let i = myArray.length - 1; i >= 0; i--) {
        tmp = myArray[i].value;
        if (tmp < lowest) lowest = tmp;
        if (tmp > highest) highest = tmp;
    }

    const circleScale = scaleLinear().domain([lowest, highest]).range([0, 25]);

    const finalData = useMemo(() => {
        let arrayObj = [];

        data.graphData.forEach(function (v) {
            let obj = { ...v };
            delete obj.lat;
            delete obj.lng;
            arrayObj.push(obj);
        });

        return arrayObj;
    }, [data.graphData]);

    const finalColumnNames = useMemo(() => {
        let obj = columnNames;

        delete obj.lat;
        delete obj.lng;

        return obj;
    }, [columnNames]);

    const validMarkers = useMemo(() => {
        return data.graphData.filter(
            (marker) =>
                marker.lat != null &&
                marker.lng != null &&
                !isNaN(marker.lat) &&
                !isNaN(marker.lng) &&
                (marker.lat !== 0 || marker.lng !== 0)
        );
    }, [data.graphData]);

    return (
        <>
            {activeTable || isExport ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={finalData}
                    columnNames={finalColumnNames}
                    userNumbersFormat={userNumbersFormat}
                    defaultTableSorting={data?.defaultTableSorting ?? null}
                />
            ) : (
                <SizeMe>
                    {({ size }) => (
                        <Tooltip
                            placement="top"
                            arrow={false}
                            content={
                                tooltipContent ? (
                                    <TooltipMapChart
                                        name={tooltipContent.city}
                                        value={tooltipContent.value}
                                        percentage={tooltipContent.percentage}
                                    />
                                ) : null
                            }
                            followCursor
                            plugins={[followCursor]}>
                            <ComposableMap
                                projectionConfig={{
                                    scale: 100
                                }}
                                width={isExport ? 740 : (size?.width ?? 0) + 100}
                                height={isExport ? 300 : height}
                                style={{
                                    width: '100%',
                                    height: 'auto'
                                }}>
                                <ZoomableGroup zoom={1}>
                                    <Geographies geography={worldMap}>
                                        {({ geographies }) =>
                                            geographies.map((geo) => {
                                                return (
                                                    <Geography
                                                        key={geo.rsmKey}
                                                        geography={geo}
                                                        fill={darkModeColorsChart[theme]['bgmap']}
                                                        stroke={
                                                            darkModeColorsChart[theme]['strokemap']
                                                        }
                                                        style={{
                                                            default: {
                                                                outline: 'none'
                                                            },
                                                            hover: {
                                                                outline: 'none'
                                                            }
                                                        }}
                                                    />
                                                );
                                            })
                                        }
                                    </Geographies>
                                    {validMarkers.map((marker) => (
                                        <Marker
                                            key={marker.city}
                                            data-testid="marker"
                                            coordinates={[marker.lng, marker.lat]}
                                            style={{
                                                default: {
                                                    outline: 'none'
                                                },
                                                hover: {
                                                    outline: 'none'
                                                }
                                            }}
                                            onMouseEnter={() => {
                                                const value = formatNumber(marker.value);
                                                const percentage = formatNumber(
                                                    marker.percentage,
                                                    getNumberFormat('percent')
                                                );
                                                setTooltipContent({
                                                    city: marker.city,
                                                    value,
                                                    percentage
                                                });
                                            }}
                                            onMouseLeave={() => {
                                                setTooltipContent(null);
                                            }}>
                                            <circle
                                                r={circleScale(marker.value)}
                                                fill={darkModeColorsChart[theme]['circlemap']}
                                                stroke={
                                                    darkModeColorsChart[theme]['circlestrokemap']
                                                }
                                                strokeWidth={2}
                                            />
                                        </Marker>
                                    ))}
                                </ZoomableGroup>
                            </ComposableMap>
                        </Tooltip>
                    )}
                </SizeMe>
            )}
        </>
    );
};

BubbleMapChartComponent.propTypes = {
    data: PropTypes.object,
    columnNames: PropTypes.object,
    activeTable: PropTypes.bool,
    userNumbersFormat: PropTypes.object
};

const BubbleMapChart = ({
    data,
    className = '',
    onReset = () => {
        /** */
    },
    isLoading = false,
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        {...restProps}>
        <BubbleMapChartComponent />
    </ChartWrapper>
);

BubbleMapChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { BubbleMapChart };
